import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useHasMenu } from '../../../auth/access';
import type { QuotationPdfFile } from '../../admin/admin.api';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import DialogShell from '../../tasks/components/DialogShell';
import QuotationPdfViewer from '../../work-implementation/reports/QuotationPdfViewer';
import {
  amt,
  AuditEntry,
  errorText,
  fetchVoucherPdf,
  fmtDate,
  fmtDateTime,
  isConflict,
  money,
  typeInfo,
  Voucher,
  vouchersApi,
} from '../accounting.api';
import VoucherEntryDialog from './VoucherEntryDialog';

export function TypeChip({ type }: { type: Voucher['type'] }) {
  const t = typeInfo(type);
  return <Chip size='small' variant='outlined' color={t.color} label={t.label} />;
}

export function CancelledChip() {
  return <Chip size='small' color='error' icon={<Icon icon='mdi:cancel' />} label='Cancelled' />;
}

const ACTIONS: Record<string, string> = { CREATE: 'Entered', ALTER: 'Altered', CANCEL: 'Cancelled' };

/**
 * A voucher opened from any screen: its lines, status and audit history, with Print, Alter
 * and Cancel for those allowed. Owns the alteration dialog, the cancel prompt and the PDF
 * viewer, so the register and every report open a voucher the same way.
 */
export default function VoucherViewer({
  companyId,
  voucherId,
  onClose,
  onChanged,
}: {
  companyId: number | null;
  /** null keeps it closed. */
  voucherId: number | null;
  onClose: () => void;
  /** After an alteration or cancellation, so the caller can reload. */
  onChanged?: () => void;
}) {
  const canAlter = useHasMenu('accounts.vouchers', 'EDIT');
  const canCancel = useHasMenu('accounts.vouchers', 'DELETE');
  const canPrint = useHasMenu('accounts.vouchers', 'EXPORT');

  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [history, setHistory] = useState<AuditEntry[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [altering, setAltering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [pdf, setPdf] = useState<QuotationPdfFile | null>(null);
  const pdfUrl = useRef<string | null>(null);

  const load = useCallback(() => {
    if (!companyId || !voucherId) return;
    setLoading(true);
    setError(null);
    setHistory(null);
    vouchersApi
      .get(companyId, voucherId)
      .then(setVoucher)
      .catch((e) => setError(errorText(e, 'Could not load the voucher', 'ACCOUNTING_VOUCHER_LOAD_FAILED')))
      .finally(() => setLoading(false));
  }, [companyId, voucherId]);

  useEffect(() => {
    setVoucher(null);
    setShowHistory(false);
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
    },
    []
  );

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && !history && companyId && voucherId) {
      vouchersApi
        .history(companyId, voucherId)
        .then(setHistory)
        .catch((e) => toast.error(errorText(e, 'Could not load the history', 'ACCOUNTING_VOUCHER_HISTORY_FAILED')));
    }
  };

  const print = async () => {
    if (!companyId || !voucher) return;
    setBusy('print');
    try {
      const file = await fetchVoucherPdf(companyId, voucher.id, voucher.voucherNo);
      if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
      pdfUrl.current = file.url;
      setPdf(file);
    } catch (e) {
      toast.error(errorText(e, 'Could not make the PDF', 'ACCOUNTING_VOUCHER_PRINT_FAILED'));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!companyId || !voucher || !reason.trim()) return;
    setBusy('cancel');
    try {
      const v = await vouchersApi.cancel(companyId, voucher.id, reason.trim(), voucher.version);
      setVoucher(v);
      setHistory(null);
      setShowHistory(false);
      setCancelling(false);
      toast.success(`Voucher ${v.voucherNo} cancelled.`);
      onChanged?.();
    } catch (e) {
      const msg = errorText(e, 'Could not cancel the voucher', 'ACCOUNTING_VOUCHER_CANCEL_FAILED');
      toast.error(isConflict(e) ? `${msg} The voucher has been reloaded.` : msg);
      if (isConflict(e)) {
        setCancelling(false);
        load();
      }
    } finally {
      setBusy(null);
    }
  };

  const cancelled = voucher?.status === 'CANCELLED';
  const totalDr = voucher?.lines.reduce((s, l) => s + Number(l.debit ?? 0), 0) ?? 0;
  const totalCr = voucher?.lines.reduce((s, l) => s + Number(l.credit ?? 0), 0) ?? 0;

  return (
    <>
      <ModalWrapper open={voucherId !== null} onClose={onClose} maxWidth='md' sx={{ p: { xs: 2, sm: 3 }, gap: 2, width: { xs: '95vw', md: 900 } }}>
        <SectionTitle
          title={voucher ? voucher.voucherNo : 'VOUCHER'}
          action={
            <IconButton onClick={onClose} aria-label='Close'>
              <Icon icon='mdi:close' />
            </IconButton>
          }
        />
        {loading && <LinearProgress />}
        {error && <Alert severity='error'>{error}</Alert>}

        {voucher && (
          <>
            <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
              <TypeChip type={voucher.type} />
              {cancelled ? <CancelledChip /> : <Chip size='small' color='success' variant='outlined' label='Posted' />}
              <Typography variant='body2'>
                <b>{fmtDate(voucher.date)}</b>
              </Typography>
              {voucher.referenceNo && (
                <Typography variant='body2' color='text.secondary'>
                  Ref. {voucher.referenceNo}
                  {voucher.referenceDate ? ` dt. ${fmtDate(voucher.referenceDate)}` : ''}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography variant='h6' fontWeight={700} sx={{ textDecoration: cancelled ? 'line-through' : undefined }}>
                ₹ {money(voucher.totalAmount)}
              </Typography>
            </Stack>

            {cancelled && <Alert severity='error'>Cancelled{voucher.cancelReason ? `: ${voucher.cancelReason}` : ''}. It no longer counts in any balance.</Alert>}

            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, opacity: cancelled ? 0.6 : 1 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Particulars</TableCell>
                    <TableCell align='right'>Debit</TableCell>
                    <TableCell align='right'>Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voucher.lines.map((l) => (
                    <TableRow key={l.lineNo}>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {Number(l.debit) > 0 ? 'Dr' : 'Cr'} · {l.ledgerName}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {l.groupName}
                          {l.narration ? ` — ${l.narration}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>{amt(l.debit)}</TableCell>
                      <TableCell align='right'>{amt(l.credit)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      {money(totalDr)}
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      {money(totalCr)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {voucher.narration && (
              <Typography variant='body2'>
                <Typography component='span' variant='body2' color='text.secondary'>
                  Narration:{' '}
                </Typography>
                {voucher.narration}
              </Typography>
            )}

            <Typography variant='caption' color='text.secondary'>
              Entered {fmtDateTime(voucher.createdAt)}
              {voucher.createdByName ? ` by ${voucher.createdByName}` : ''}
              {voucher.version > 1 && voucher.updatedAt
                ? ` · Last changed ${fmtDateTime(voucher.updatedAt)}${voucher.updatedByName ? ` by ${voucher.updatedByName}` : ''}`
                : ''}
            </Typography>

            <Box>
              <Button size='small' onClick={toggleHistory} startIcon={<Icon icon={showHistory ? 'mdi:chevron-up' : 'mdi:history'} />}>
                {showHistory ? 'Hide history' : 'History'}
              </Button>
              <Collapse in={showHistory}>
                {!history ? (
                  <LinearProgress sx={{ mt: 1 }} />
                ) : (
                  <Stack gap={0.5} sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                    {history.length === 0 && (
                      <Typography variant='body2' color='text.secondary'>
                        No history recorded.
                      </Typography>
                    )}
                    {history.map((h, i) => (
                      <Typography key={i} variant='body2'>
                        <b>{ACTIONS[h.action] ?? h.action}</b> (v{h.version}) · {fmtDateTime(h.at)}
                        {h.userName ? ` · ${h.userName}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Collapse>
            </Box>

            <Stack direction='row' gap={1} flexWrap='wrap' justifyContent='flex-end'>
              {canCancel && !cancelled && (
                <Button
                  color='error'
                  variant='outlined'
                  startIcon={<Icon icon='mdi:cancel' />}
                  disabled={busy !== null}
                  onClick={() => {
                    setReason('');
                    setCancelling(true);
                  }}
                >
                  Cancel voucher
                </Button>
              )}
              {canAlter && !cancelled && (
                <Button variant='outlined' startIcon={<Icon icon='mdi:pencil' />} disabled={busy !== null} onClick={() => setAltering(true)}>
                  Alter
                </Button>
              )}
              {canPrint && (
                <Button variant='contained' startIcon={<Icon icon='mdi:printer' />} disabled={busy !== null} onClick={print}>
                  {busy === 'print' ? 'Preparing…' : 'Print'}
                </Button>
              )}
            </Stack>
          </>
        )}
      </ModalWrapper>

      <VoucherEntryDialog
        open={altering}
        companyId={companyId}
        voucher={altering ? voucher : null}
        onClose={() => setAltering(false)}
        onSaved={(v) => {
          setVoucher(v);
          setHistory(null);
          setShowHistory(false);
          onChanged?.();
        }}
      />

      <DialogShell
        open={cancelling}
        title='CANCEL VOUCHER'
        onClose={() => setCancelling(false)}
        onSubmit={cancel}
        submitLabel='Cancel voucher'
        submitColor='error'
        busy={busy === 'cancel'}
        disabled={!reason.trim()}
      >
        <Typography variant='body2'>
          {voucher?.voucherNo} stays in the register, marked cancelled, and stops counting in every balance. This cannot be undone.
        </Typography>
        <TextField
          label='Reason'
          size='small'
          required
          autoFocus
          multiline
          minRows={2}
          value={reason}
          inputProps={{ maxLength: 255 }}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogShell>

      <QuotationPdfViewer
        file={pdf}
        title={pdf?.filename ?? 'Voucher PDF'}
        onClose={() => {
          if (pdfUrl.current) URL.revokeObjectURL(pdfUrl.current);
          pdfUrl.current = null;
          setPdf(null);
        }}
      />
    </>
  );
}
